// CSV → deck items, for the My Library CSV import on-ramp. Pure (no DOM) so it's
// unit-tested. parseCsv is a small RFC-4180 parser (handles quoted fields with
// embedded commas/newlines and "" escapes); mapRowsToItems turns rows into the
// item shape POST /api/library/decks expects, reporting per-row problems.

const LETTERS = ["A", "B", "C", "D"];

// Parse CSV text → { headers: string[], rows: object[] } where each row is keyed
// by the lowercased, trimmed header.
// Low-level RFC-4180 split → array of records (string[][]). No header
// interpretation — callers decide whether record 0 is a header (parseImport.js
// needs this for headerless-file detection). Blank records are dropped.
export function parseRecords(text) {
  const s = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const records = [];
  let field = "", row = [], inQuotes = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\n") {
      row.push(field); records.push(row); row = []; field = "";
    } else {
      field += c;
    }
  }
  if (field !== "" || row.length) { row.push(field); records.push(row); }
  return records.filter((r) => r.some((c) => c.trim() !== ""));
}

export function parseCsv(text) {
  const nonEmpty = parseRecords(text);
  if (nonEmpty.length === 0) return { headers: [], rows: [] };

  const headers = nonEmpty[0].map((h) => h.trim());
  const keys = headers.map((h) => h.toLowerCase());
  const rows = nonEmpty.slice(1).map((r) => {
    const o = {};
    keys.forEach((k, idx) => { o[k] = (r[idx] ?? "").trim(); });
    return o;
  });
  return { headers, rows };
}

function pick(row, aliases) {
  for (const a of aliases) if (a in row && row[a] !== "") return row[a];
  return "";
}

// Recognised columns (case-insensitive). question + 4 options + correct are
// required; difficulty / type / tags optional. `correct` accepts a letter
// (A–D), a number (1–4), or the exact option text.
export function mapRowsToItems(rows) {
  const items = [];
  const errors = [];
  (rows || []).forEach((row, i) => {
    const line = i + 2; // +1 for 0-index, +1 for the header row
    const question = pick(row, ["question", "q", "prompt"]);
    const options = [
      pick(row, ["optiona", "a", "option1", "1"]),
      pick(row, ["optionb", "b", "option2", "2"]),
      pick(row, ["optionc", "c", "option3", "3"]),
      pick(row, ["optiond", "d", "option4", "4"])
    ];
    const correctRaw = pick(row, ["correct", "answer", "correctanswer"]);
    const difficulty = pick(row, ["difficulty", "diff"]);
    const type = pick(row, ["type"]);
    const tagsRaw = pick(row, ["tags", "categories"]);

    const rowErrors = [];
    if (!question) rowErrors.push("missing question");
    if (options.some((o) => !o)) rowErrors.push("needs 4 non-empty options (A–D)");

    let correctIndex = -1;
    const cr = correctRaw.trim();
    if (/^[A-Da-d]$/.test(cr)) correctIndex = LETTERS.indexOf(cr.toUpperCase());
    else if (/^[1-4]$/.test(cr)) correctIndex = Number(cr) - 1;
    else if (cr) correctIndex = options.findIndex((o) => o && o.toLowerCase() === cr.toLowerCase());
    if (correctIndex < 0) rowErrors.push(`unrecognised correct answer "${correctRaw}" (use A–D, 1–4, or the exact option text)`);

    if (rowErrors.length) { errors.push({ line, errors: rowErrors }); return; }

    items.push({
      question,
      options,
      correctIndex,
      difficulty: /^[1-5]$/.test(difficulty) ? Number(difficulty) : undefined,
      type: type || undefined,
      tags: tagsRaw ? tagsRaw.split(/[;|]/).map((t) => t.trim()).filter(Boolean) : undefined
    });
  });
  return { items, errors };
}
